import { Dispatch, RefObject, useContext, useEffect, useRef, useState } from "react";
import { DispSize, WindowContext } from "./WindowContextProvider";
import { MenuButton } from "./MenuButton";

export interface MenuData {
    id: string,
    name: string,
    clickCallback: (menuId:string) => void,
    offset?: {x:number, y:number},
}

export interface MainMenuProps {
    data: MenuData[],
    hh:number,
    setVisible: Dispatch<boolean>,
}

export function generateNoiseCanvas(width:number, height:number) {
    const offscreenCanvas = document.createElement('canvas');
    const offscreenCtx = offscreenCanvas.getContext('2d');
    if(!offscreenCtx) return null;
    offscreenCanvas.width = width;
    offscreenCanvas.height = height;
    const imageData = offscreenCtx.createImageData(width, height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
        const alpha = Math.random() * 40 + 150;
        data[i] = 255;
        data[i + 1] = 255;
        data[i + 2] = 255;
        data[i + 3] = alpha;
    }
    offscreenCtx.putImageData(imageData, 0, 0);
    return offscreenCanvas;
}

function quadraticCurve(x0:number,y0:number,xc:number,yc:number,x1:number,y1:number,
    t:number) {
    var res = {x: 0, y: 0};
    res.x = Math.pow(1-t,2)*x0+2*(1-t)*t*xc+Math.pow(t,2)*x1;
    res.y = Math.pow(1-t,2)*y0+2*(1-t)*t*yc+Math.pow(t,2)*y1;
    return res;
}

export const MainMenu = ({data,hh,setVisible}: MainMenuProps) : React.JSX.Element => {
    const ds: DispSize = useContext(WindowContext);
    const hgref: RefObject<HTMLCanvasElement | null> = useRef(null);
    const divref: RefObject<HTMLDivElement | null> = useRef(null);
    const [x,setX] = useState<number>(0);
    const [fs,setFS] = useState<number>(20);
    var animStep = 0;
    var highlited = false;
    
    const calculateMMSize = () => {
        const ctx = hgref.current?.getContext('2d');
        var _fs = 0.03*ds.height;
        if(_fs < 16) _fs = 16;
        if(_fs > 0.05*ds.height) _fs = 0.05*ds.height;
        if(ctx !== null && ctx !== undefined) {
            ctx.font = `bold ${_fs}px KoHo`;
            var minw = 0;
            var updated = false;
            data.forEach((item) => {
                var eimt1 = ctx.measureText(item.name);
                var fsw1 = Math.abs(eimt1.actualBoundingBoxLeft) + Math.abs(eimt1.actualBoundingBoxRight);
                var fsh1 = Math.abs(eimt1.actualBoundingBoxAscent) + Math.abs(eimt1.actualBoundingBoxDescent);
                while(fsw1 > 2.3*(ds.width/3)-2*bh && _fs > 0) {
                    _fs -= 2;
                    ctx.font = `bold ${_fs}px KoHo`;
                    eimt1 = ctx.measureText(item.name);
                    fsw1 = Math.abs(eimt1.actualBoundingBoxLeft) + Math.abs(eimt1.actualBoundingBoxRight);
                    fsh1 = Math.abs(eimt1.actualBoundingBoxAscent) + Math.abs(eimt1.actualBoundingBoxDescent);
                }
                if(fsw1*2 > minw) {
                    minw = fsw1*2;
                    updated = true;
                }
            });
            if(minw > 0 && minw <= ds.width) return {fs: _fs, width: minw};
        }
        return {fs: _fs, width: ds.width};
    }
    
    const cs0 = calculateMMSize();
    //var fs = cs0.fs;
    var mmdh = data.length*(2*(hh/3)), mmdw = cs0.width, mmls = mmdw / 3;
    var bh = (hh/3);
    const calcGeometry = () => {
        const cs1 = calculateMMSize();
        setFS(cs1.fs);
        mmdw = cs1.width;
        mmls = mmdw / 3;
        const qstep = 1/((mmdh-bh)/bh);
        var xstep = qstep;
        data.forEach((item) => {
            const elp = quadraticCurve(mmls,0,mmls,mmdh/2,0,mmdh-bh,xstep);
            item.offset = elp;
            xstep += 2*qstep;
        });
    };

    const paintMMContainer = () => {
        const ctx = hgref.current?.getContext('2d');
        if(ctx !== null && ctx !== undefined) {
            ctx.clearRect(0, 0, mmdw, mmdh);
            ctx.imageSmoothingEnabled = true; 

            const noiseCanvas = null;//generateNoiseCanvas(mmdw, mmdh);

            ctx.beginPath();
            ctx.moveTo(mmls,0);
            ctx.quadraticCurveTo(mmls,mmdh/2,0,mmdh-bh);
            ctx.lineTo(bh,mmdh);
            ctx.lineTo(mmdw,mmdh);
            ctx.lineTo(mmdw,0);
            ctx.lineTo(mmls,0);
            ctx.closePath();
        
            ctx.save();
            ctx.clip();
            if(noiseCanvas !== null) ctx.drawImage(noiseCanvas, 0, 0, mmdw,mmdh);
            else {
                ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
                ctx.fill();
            }
            ctx.restore();
        }
    };

    const timerHandler = () => {
        if(animStep < mmdh) {
            animStep += mmdh/20;
            if(animStep > mmdh) animStep = mmdh;
            if(divref.current)
                divref.current.style.height = animStep+"px";
            setTimeout(timerHandler, 10);
        }
    };

    useEffect(() => {
        calcGeometry();
    }, []);

    useEffect(() => {
        calcGeometry();
        if(hgref.current) {
            hgref.current.width = mmdw;
            hgref.current.height = mmdh;
        }
        if(divref.current) {
            divref.current.style.width = `${mmdw}px`;
            divref.current.style.height = `0px`;
        }
        animStep = 0;
        paintMMContainer();
        setTimeout(timerHandler, 10);
        setX(x+1);
    }, [ds]);

    const focused = () => {
        console.log('menu focused');
        highlited = true;
    };
    const blurred = () => {
        console.log('menu blurred');
        if(highlited) {
            highlited = false;
            setVisible(false);
        }
    };

    return(
        <div className="MainMenuContainer" 
            style={{top: `${hh}px`, right: 0, width: `${mmdw}px`, height: '0px', zIndex: 91}}
            ref={divref} onMouseEnter={focused} onMouseLeave={blurred}>
            {data.map((item,i) => 
                <MenuButton bh={bh} bw={mmdw} fs={fs} data={{...item}} key={i} />
            )}
            <canvas style={{position: "absolute", width: '100%', left: 0, top: 0, zIndex: 92}} ref={hgref} />
        </div>
    );
}